import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AdminShell } from "@/components/admin-shell";
import { AuditLogsPage } from "@/pages/audit-logs-page";
import { BookingsPage } from "@/pages/bookings-page";
import { ContentPage } from "@/pages/content-page";
import { CreditsPage } from "@/pages/credits-page";
import { DashboardPage } from "@/pages/dashboard-page";
import { LoginPage } from "@/pages/login-page";
import { MfaSetupPage } from "@/pages/mfa-setup-page";
import { MfaVerifyPage } from "@/pages/mfa-verify-page";
import { PlansPage } from "@/pages/plans-page";
import { ProductsPage } from "@/pages/products-page";
import { PromotionsPage } from "@/pages/promotions-page";
import { ReportsPage } from "@/pages/reports-page";
import { StoresPage } from "@/pages/stores-page";
import { UsersPage } from "@/pages/users-page";
import { WebhooksPage } from "@/pages/webhooks-page";
import { useAdminAuth } from "@/lib/auth/auth-context";

function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAuthenticated, isHydrated } = useAdminAuth();

  if (!isHydrated) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <p className="text-sm text-neutral-500">กำลังตรวจสอบสิทธิ์...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AdminShell>{children}</AdminShell>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mfa/verify" element={<MfaVerifyPage />} />
      <Route path="/mfa/setup" element={<MfaSetupPage />} />
      <Route
        path="/"
        element={
          <RequireAdmin>
            <DashboardPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireAdmin>
            <DashboardPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAdmin>
            <UsersPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/stores"
        element={
          <RequireAdmin>
            <StoresPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/products"
        element={
          <RequireAdmin>
            <ProductsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/bookings"
        element={
          <RequireAdmin>
            <BookingsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAdmin>
            <ReportsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/credits"
        element={
          <RequireAdmin>
            <CreditsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/plans"
        element={
          <RequireAdmin>
            <PlansPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/promotions"
        element={
          <RequireAdmin>
            <PromotionsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/content"
        element={
          <RequireAdmin>
            <ContentPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <RequireAdmin>
            <AuditLogsPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/webhooks"
        element={
          <RequireAdmin>
            <WebhooksPage />
          </RequireAdmin>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
